import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { queryClient } from '@/lib/query-client';
import { rolePermissionsService } from '@/services/rolePermissions.service';
import type { RoleName } from '@/stores/permissions.store';

const ROLE_PERMISSIONS_QUERY_KEY = ['admin', 'role-permissions'] as const;
const ME_PERMISSIONS_QUERY_KEY = ['me', 'permissions'] as const;

const rolePermissionsQueryAtom = atomWithQuery(() => ({
	queryKey: ROLE_PERMISSIONS_QUERY_KEY,
	queryFn: () => rolePermissionsService.getAll(),
	staleTime: 60 * 1000
}));

const updateRolePermissionsMutationAtom = atomWithMutation(() => ({
	mutationKey: ['admin', 'role-permissions', 'update'],
	mutationFn: ({ role, permissions }: { role: RoleName; permissions: string[] }) =>
		rolePermissionsService.updateRole(role, permissions),
	onSuccess: () => {
		queryClient.invalidateQueries({ queryKey: ROLE_PERMISSIONS_QUERY_KEY });
		queryClient.invalidateQueries({ queryKey: ME_PERMISSIONS_QUERY_KEY });
	}
}));

export function useRolePermissions() {
	const atom = useMemo(() => rolePermissionsQueryAtom, []);
	const [query] = useAtom(atom);
	return query;
}

export function useUpdateRolePermissions() {
	const [mutation] = useAtom(updateRolePermissionsMutationAtom);
	return mutation;
}
